import { useEffect, useRef } from "react";
import L from "leaflet";

const DEFAULT_CENTER: L.LatLngExpression = [30.0444, 31.2357];
const DEFAULT_ZOOM = 14;
const MIN_ZOOM = 6;
const PIN_DROP_SPAN = 0.08;
// Egypt's broad national bounds, including the Nile Valley, Delta and Sinai.
const EGYPT_BOUNDS = L.latLngBounds([21.5, 24.0], [31.8, 37.0]);

export function isEgyptCoordinate(latitude: number, longitude: number) {
  return Number.isFinite(latitude) && Number.isFinite(longitude) && EGYPT_BOUNDS.contains([latitude, longitude]);
}

function buildMarkerIcon() {
  return L.divIcon({
    className: "",
    html: `
      <div style="
        width: 28px;
        height: 28px;
        border-radius: 9999px;
        border: 3px solid white;
        background: var(--color-signal, #5b2cff);
        box-shadow: 0 4px 12px rgba(0,0,0,0.35);
        display: grid;
        place-items: center;
      ">
        <div style="
          width: 10px;
          height: 10px;
          border-radius: 9999px;
          background: var(--color-accent, #b6ff2e);
        "></div>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

export function VenueMapPicker({
  latitude,
  longitude,
  onChange,
  height = "10rem",
}: {
  latitude: number | null;
  longitude: number | null;
  onChange: (latitude: number, longitude: number) => void;
  height?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const lastValidPositionRef = useRef<L.LatLngExpression>(DEFAULT_CENTER);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const requestedCenter: L.LatLngExpression =
      latitude != null && longitude != null ? [latitude, longitude] : DEFAULT_CENTER;
    const initialCenter =
      latitude != null && longitude != null && isEgyptCoordinate(latitude, longitude)
        ? requestedCenter
        : DEFAULT_CENTER;

    const map = L.map(containerRef.current, {
      attributionControl: true,
      zoomControl: true,
      maxBounds: EGYPT_BOUNDS,
      maxBoundsViscosity: 1,
      minZoom: MIN_ZOOM,
    }).setView(initialCenter, DEFAULT_ZOOM);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker(initialCenter, { icon: buildMarkerIcon(), draggable: true }).addTo(map);
    lastValidPositionRef.current = initialCenter;

    marker.on("dragend", () => {
      const position = marker.getLatLng();
      if (!isEgyptCoordinate(position.lat, position.lng)) {
        marker.setLatLng(lastValidPositionRef.current);
        return;
      }
      lastValidPositionRef.current = position;
      onChange(position.lat, position.lng);
    });

    map.on("click", (event) => {
      const { lat, lng } = event.latlng;
      if (!isEgyptCoordinate(lat, lng)) {
        return;
      }
      marker.setLatLng([lat, lng]);
      lastValidPositionRef.current = [lat, lng];
      onChange(lat, lng);
    });

    mapRef.current = map;
    markerRef.current = marker;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker || latitude == null || longitude == null || !isEgyptCoordinate(latitude, longitude)) {
      return;
    }

    const current = marker.getLatLng();
    const distance = Math.hypot(current.lat - latitude, current.lng - longitude);
    marker.setLatLng([latitude, longitude]);
    lastValidPositionRef.current = [latitude, longitude];

    if (distance > PIN_DROP_SPAN / 2) {
      map.setView([latitude, longitude], map.getZoom());
    }
  }, [latitude, longitude]);

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-[18px] border border-white/10"
      style={{ height }}
    />
  );
}
